import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { FormGroup } from '@angular/forms'
import { Observable, of, take, tap } from 'rxjs'
import { ContentUnitService } from './content-unit.service'
import { ContentUnitFormService } from './content-unit-form.service'
import { QuillHelperService } from './quill-helper.service'
import { PlaceholderService } from './placeholder.service'
import { ContentUnitStateService } from '@app/layout/sidebar/services/content-unit-state.service'
import { ModalHelperService } from '@services/modal-helper.service'
import { GlobalErrorService } from '@services/global-error.service'
import { ContentUnitApiResponse, ContentUnitForm, ContentUnitTemplateApiResponse } from '../models/content-unit.model'
import { Placeholder } from '../models/placeholder.model'
import { ContentState } from '@core/utils/route-utils'
import Quill from 'quill'

@Injectable()
export class ContentUnitManagerService {
  // Component State Signals
  public readonly contentState = signal<ContentState>('view')
  public readonly isReadOnly = signal(false)
  public readonly isCodeView = signal(false)
  public readonly placeholders = signal<Placeholder[]>([])
  public readonly suggestedTags = signal<string[]>([])
  public readonly contentUnitForm: FormGroup<ContentUnitForm>
  public quill: Quill | null = null
  // Computed Signals for derived state
  public readonly isViewMode = computed(() => this.contentState() === 'view')
  public readonly isEditMode = computed(() => this.contentState() === 'edit')
  public readonly isCreateMode = computed(() => this.contentState() === 'create')
  public readonly templates = signal<ContentUnitTemplateApiResponse[]>([])
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly contentUnitService = inject(ContentUnitService)
  private readonly formService = inject(ContentUnitFormService)
  private readonly quillHelper = inject(QuillHelperService)
  private readonly placeholderService = inject(PlaceholderService)
  private readonly contentUnitState = inject(ContentUnitStateService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly modalHelper = inject(ModalHelperService)

  constructor() {
    this.contentUnitForm = this.formService.buildForm()
    this.loadInitialData()
  }

  public loadContentUnit(uuid: string): void {
    this.contentUnitService.getById(uuid).pipe(take(1)).subscribe({
      next: data => {
        const visualContent = this.quillHelper.convertToVisual(data.content, this.placeholders())
        this.contentUnitForm.patchValue({
          ...data,
          content: this.isCodeView() ? data.content : visualContent
        })
        this.contentUnitForm.markAsPristine()
        this.formService.addAsyncValidators(this.contentUnitForm, data.title)
        if (this.quill && !this.isCodeView()) {
          this.quill.clipboard.dangerouslyPasteHTML(visualContent)
        }
      },
      error: () => this.globalErrorService.reportError({
        subsystemName: 'Content Unit Loading',
        description: 'The Content Unit could not be loaded.'
      })
    })
  }

  public saveContentUnit(): void {
    if (this.contentUnitForm.invalid) return

    this.performSave$().subscribe({
      error: () => this.reportSaveError()
    })
  }

  public performSave$(): Observable<ContentUnitApiResponse> {
    const rawValue = this.contentUnitForm.getRawValue()
    let content = rawValue.content ?? ''

    if (!this.isCodeView() && this.quill) {
      content = this.quillHelper.convertToCode(this.quill.root.innerHTML)
    }

    const data: ContentUnitApiResponse = {
      uuid: rawValue.uuid,
      title: rawValue.title ?? '',
      description: rawValue.description ?? '',
      tags: rawValue.tags ?? [],
      content: content,
      skipPlaceholder: rawValue.skipPlaceholder ?? false
    }

    return this.contentUnitService.save(data).pipe(
      take(1),
      tap(res => {
        this.contentUnitForm.patchValue({
          ...res,
          content: this.isCodeView() ? res.content : this.quillHelper.convertToVisual(res.content, this.placeholders())
        })
        this.contentUnitForm.markAsPristine()
        if ((typeof data.uuid !== 'string' || data.uuid === '') && (typeof res.uuid === 'string' && res.uuid !== '')) {
          this.formService.addAsyncValidators(this.contentUnitForm, res.title)
          this.contentUnitState.reload().subscribe()
          this.isReadOnly.set(true)
          this.contentState.set('view')
          void this.router.navigate(['/content-unit', res.uuid])
        } else {
          this.triggerPageReload()
        }
      })
    )
  }

  public cancelEdit(): void {
    if (this.isReadOnly() || !this.isEditMode()) {
      this.contentUnitForm.markAsPristine()
      void this.router.navigate(['/content-unit'])
    } else {
      this.isReadOnly.set(true)
      this.contentState.set('view')
      const uuid = this.contentUnitForm.getRawValue().uuid
      if (typeof uuid === 'string' && uuid !== '') {
        this.loadContentUnit(uuid)
      }
    }
  }

  public searchTags(query: string): void {
    this.contentUnitService.getTags(query).pipe(take(1)).subscribe(tags => {
      if (tags.some(t => t.toLowerCase() === query.toLowerCase())) {
        this.suggestedTags.set(tags)
      } else {
        this.suggestedTags.set([query, ...tags])
      }
    })
  }

  public toggleCodeView(): void {
    const control = this.contentUnitForm.get('content')
    if (!control) return

    if (this.isCodeView()) {
      const code = (control.value) ?? ''
      const visual = this.quillHelper.convertToVisual(code, this.placeholders())
      control.setValue(visual, { emitEvent: false })
      if (this.quill) {
        this.quill.clipboard.dangerouslyPasteHTML(visual)
      }
    } else {
      const visual = this.quill ? this.quill.root.innerHTML : (control.value) ?? ''
      const code = this.quillHelper.convertToCode(visual)
      control.setValue(code, { emitEvent: false })
    }
    this.isCodeView.set(!this.isCodeView())
  }

  public confirmLeave(): Observable<boolean> {
    return this.modalHelper.confirmLeave(() => {
      if (this.contentUnitForm.invalid) {
        this.contentUnitForm.markAllAsTouched()
        return of(false)
      }
      return this.performSave$()
    }, () => this.reportSaveError())
  }

  public triggerPageReload(): void {
    globalThis.location.reload()
  }

  public close(): void {
    void this.router.navigate(['/content-unit'])
  }

  public getTemplatesByContentUnitId(uuid: string): void {
    this.contentUnitService.getTemplatesByContentUnitId(uuid).pipe(take(1)).subscribe({
      next: (templates) => {
        this.templates.set(templates)
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Template Loading',
          description: 'The Templates for the Content Unit cannot not be fetched.'
        })
      }
    })
  }

  private reportSaveError(): void {
    this.globalErrorService.reportError({
      subsystemName: 'Content Unit Saving',
      description: 'The Content Unit could not be saved.'
    })
  }

  private loadInitialData(): void {
    this.placeholderService.getPlaceholders().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(placeholders => {
      this.placeholders.set(placeholders)

      // Re-sync content once placeholders are loaded to ensure labels are correct
      const control = this.contentUnitForm.get('content')
      if (control && !this.isCodeView()) {
        const currentContent = control.value as string ?? ''
        const code = this.quillHelper.convertToCode(currentContent)
        const visual = this.quillHelper.convertToVisual(code, placeholders)
        if (currentContent !== visual) {
          control.setValue(visual, { emitEvent: false })
          if (this.quill) {
            this.quill.clipboard.dangerouslyPasteHTML(visual)
          }
        }
      }
    })

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const uuid = params['uuid'] as string | undefined
      if (typeof uuid === 'string' && uuid !== '') {
        this.isReadOnly.set(true)
        this.loadContentUnit(uuid)
        this.getTemplatesByContentUnitId(uuid)
      } else {
        this.isReadOnly.set(false)
        this.contentUnitForm.reset()
        this.formService.addAsyncValidators(this.contentUnitForm)
      }
    })
  }

}
